import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { LegacyImageFormat } from '../../../models/new/legacy-image-format';
import { LegacyImageFormatMapper } from '../../../services/legacy-image-format-mapper';
import {
	LegacyImageFormatModalComponent,
	LegacyImageFormatModalData,
	LegacyImageFormatModalResult
} from './legacy-image-format-modal/legacy-image-format-modal.component';

@Component({
	standalone: false,
	selector: 'bt-company-legacy-image-formats',
	templateUrl: './company-legacy-image-formats.component.html',
	styleUrls: ['./company-legacy-image-formats.component.scss']
})
export class CompanyLegacyImageFormatsComponent implements OnInit {
	formats: LegacyImageFormat[] = [];
	loading = false;

	constructor(
		private graphQLService: GraphQLService,
		private utilitiesService: UtilitiesService,
		private legacyImageFormatMapper: LegacyImageFormatMapper,
		private router: Router,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.getLegacyImageFormats();
	}

	getLegacyImageFormats(): void {
		this.loading = true;
		this.graphQLService
			.getData<any>(TemplatesQueries.GET_LEGACY_IMAGE_FORMATS)
			.pipe(
				map((res: any) =>
					(res.data.legacyImageFormats || []).map((node: any) => this.legacyImageFormatMapper.toLegacyImageFormat(node))
				)
			)
			.subscribe({
				next: (formats: LegacyImageFormat[]) => {
					this.formats = formats;
					this.loading = false;
				},
				error: () => {
					this.loading = false;
					this.utilitiesService.showSnackbar('An error occurred while loading the legacy image formats', SnackbarType.Error);
				}
			});
	}

	onBack(): void {
		this.router.navigate(['/company/third-parties-applications']);
	}

	openEditFormatModal(format: LegacyImageFormat): void {
		const data: LegacyImageFormatModalData = { format, formats: this.formats };
		this.dialog
			.open(LegacyImageFormatModalComponent, { data })
			.afterClosed()
			.subscribe((result?: LegacyImageFormatModalResult) => {
				if (result) {
					this.updateFormat(format, result);
				}
			});
	}

	private updateFormat(format: LegacyImageFormat, result: LegacyImageFormatModalResult): void {
		this.loading = true;
		const input = this.legacyImageFormatMapper.toUpdateInput(format, result.size, result.active);
		this.graphQLService.postData(MutationQuery.UPDATE_LEGACY_IMAGE_FORMAT, { input }).subscribe({
			next: (res: any) => {
				const response = res?.data?.updateLegacyImageFormat;
				const errorMessage = this.getGraphQlErrorMessage(res) || (response && !response.success ? response.message : undefined);
				if (errorMessage) {
					this.loading = false;
					this.utilitiesService.showSnackbar(errorMessage, SnackbarType.Error);
					return;
				}

				this.utilitiesService.showSnackbar(`Format ${format.code} saved successfully`, SnackbarType.Success);
				this.getLegacyImageFormats();
			},
			error: (err: any) => {
				this.loading = false;
				this.utilitiesService.showSnackbar(
					this.getGraphQlErrorMessage(err) || `An error occurred while saving format ${format.code}`,
					SnackbarType.Error
				);
			}
		});
	}

	private getGraphQlErrorMessage(err: any): string | undefined {
		const graphQlErrors = [
			...(err?.errors || []),
			...(err?.networkError?.error?.errors || []),
			...(err?.networkError?.result?.errors || []),
			...(err?.networkError?.errors || [])
		];

		for (const graphQlError of graphQlErrors) {
			const extensions = graphQlError?.extensions;
			const message = extensions?.Message || extensions?.message || graphQlError?.message;
			if (typeof message === 'string' && message.trim()) {
				return message.trim();
			}
		}

		return undefined;
	}
}
